import { isEmpty } from "lodash";
import { errorView } from "../../../../helpers/ErrorHandler";
import { checkYearValidation, validateCharacterLength, validateFacilityNumber } from "../../../../utils/utils";
import { EnergyDraft, FacilityDraftFormValues } from "../types/facilityDrawer.types";

export const facilityNameRules = {
  required: "Facility name is required",
  validate: {
    maxLength: (value: string) => validateCharacterLength(value, 32, "Name cannot exceed 32 characters")
  }
};

export const facilityNumberRules = {
  validate: {
    valueCheck: (v: any) => validateFacilityNumber(v)
  }
};

// Named "facilityYearValid" (not "yearValid") deliberately: FormInputNumber's isYearInput
// path injects its own `validate.yearValid` key via `{...rules.validate, yearValid: builtin}` —
// if we reused that name, the built-in assignment would silently clobber this one instead of
// running alongside it. Because ours runs first and RHF's default criteriaMode ("firstError")
// stops at the first failing validator, the two never disagree in practice (see FacilityYearInput).
export const yearRules = {
  required: "Year is required",
  validate: {
    facilityYearValid: (v: any) => checkYearValidation(Number(v)) || "Please enter a valid year"
  }
};

export const energyPercentageRules = {
  required: "Percentage is required",
  validate: {
    valueCheck: (v: any) => !v || Number(v) <= 100 || "Percentage should be less than or equal to 100"
  }
};

/**
 * Returns true when the draft is blocked from saving, firing the same errorView
 * toast (message + ordering) the legacy register/edit modals used.
 */
export const checkFacilitySaveCondition = (
  mode: "register" | "edit",
  draft: FacilityDraftFormValues
): boolean => {
  if (mode === "edit" && isEmpty(draft.facilityName.trim())) {
    errorView("Facility name is required");
    return true;
  }

  if (!draft.facilityCountry) {
    errorView("Facility country is required", { force: true });
    return true;
  }

  if (draft.energy.length <= 0) {
    errorView("Please add facility energy", { force: true });
    return true;
  }

  return false;
};

export const computeYearDistributionTotals = (energy: EnergyDraft[]): Map<string, number> => {
  const totals = new Map<string, number>();

  energy.forEach(row => {
    const year = row.Year || "";
    const percentage = Number(row.Percentage) || 0;
    totals.set(year, (totals.get(year) ?? 0) + percentage);
  });

  return totals;
};
